/**
 * Shared primitives + formatters for the booking-detail cards (BON-145), used by
 * WorkshopSummaryCard and BookingDetailCards. Kept in one place so the card
 * files stay small and consistent.
 */
import type { ReactNode } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';

export const cardCls =
  'bg-white dark:bg-[#1e2521] rounded-xl border border-stone-200 dark:border-stone-700';

export const META = 'text-[11px] font-semibold uppercase tracking-wide text-stone-400';

export function fmtWorkshopWhen(workshop: BookingDetail['workshop']): string {
  // localStartDatetime is naive wall-clock (no tz) → parsed as local, which is
  // exactly the venue-local time we want to show. Fall back to the UTC instant.
  const iso = workshop.localStartDatetime ?? workshop.startsAt;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function fmtCreatedDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

export function initials(first: string | null, last: string | null, email: string | null): string {
  const combined = `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase();
  if (combined.trim() !== '') return combined;
  return (email ?? '?').charAt(0).toUpperCase();
}

export function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-stone-200 dark:border-stone-700 text-xs text-stone-600 dark:text-stone-300">
      {children}
    </span>
  );
}

export function InfoLine({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-sm text-stone-600 dark:text-stone-300">
      <span className="flex-shrink-0 text-stone-400">{icon}</span>
      <span className="min-w-0">{children}</span>
    </p>
  );
}
